import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Text, useTheme } from '@ltvco/refresh-lib/theme';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  ChevronRight,
} from '@mui/icons-material';
import React, { useContext, useState } from 'react';

import {
  CardContainer,
  HeaderStack,
  ExpandButton,
  AdditionalDetailsBox,
  DetailsBox,
  SecondaryTextTypography,
} from './common/components';
import { scrollToCard } from './common/utils';

export interface BaseCardProps {
  bvid?: string;
  name?: string;
  isExpandable?: boolean;
  onDetailsClick?: () => void;
  renderIcon: () => React.ReactNode;
  renderHeader: () => React.ReactNode;
  renderExpandedContent?: () => React.ReactNode;
  footerText?: string;
  showFooter?: boolean;
  tabName?: string;
}

export const BaseCard: React.FC<BaseCardProps> = ({
  isExpandable = true,
  onDetailsClick,
  renderIcon,
  renderHeader,
  renderExpandedContent,
  footerText,
  showFooter = true,
  tabName,
}) => {
  const theme = useTheme();
  const { trackEventGA4 } = useContext(AppConfig);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isExpanded) {
      const target = event.target as HTMLElement;
      const cardElement = target.closest('.base-card-container');
      if (cardElement) {
        scrollToCard(cardElement);
      }
    }

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: isExpanded ? 'collapse' : 'expand',
      interacted_from: tabName ?? '',
    });

    setIsExpanded(!isExpanded);
  };

  // Circled chevron component - positioned absolutely
  const CircledChevron = ({ onClick }: { onClick?: () => void }) => (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      sx={{
        position: 'absolute',
        right: 16,
        top: 16,
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        border: `1px solid ${theme.palette.grey[300]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <ChevronRight sx={{ color: theme.palette.grey[500], fontSize: '20px' }} />
    </Box>
  );

  return (
    <CardContainer className="base-card-container">
      {/* Chevron positioned absolutely - always in same spot */}
      {onDetailsClick && <CircledChevron onClick={onDetailsClick} />}

      <HeaderStack
        onClick={onDetailsClick ? onDetailsClick : undefined}
        sx={{ paddingRight: onDetailsClick ? '48px' : 0 }} // Reserve space for chevron
      >
        {renderIcon()}
        <Box
          sx={{
            flex: 1,
            minWidth: 0, // Allow text truncation
          }}
          mt="4px"
          ml="14px"
        >
          {renderHeader()}
        </Box>
      </HeaderStack>

      {isExpandable && !isExpanded ? (
        <ExpandButton
          onClick={handleExpandToggle}
          sx={{ cursor: 'pointer', mt: 2 }}
        >
          <Text variant="body2" fontWeight="bold" color="primary">
            Expand Details
          </Text>
          <KeyboardArrowDown
            fontSize="small"
            sx={{ marginLeft: '2px', color: theme.palette.primary.main }}
          />
        </ExpandButton>
      ) : isExpandable && renderExpandedContent ? (
        <>
          {renderExpandedContent()}

          {showFooter && footerText && onDetailsClick && (
            <AdditionalDetailsBox
              onClick={() => {
                trackEventGA4({
                  event_name: 'search_results_interaction',
                  type: 'card click',
                  action: 'additional cta',
                  interacted_from: tabName ?? '',
                });
                onDetailsClick();
              }}
            >
              <DetailsBox>
                <Box sx={{ flexDirection: 'column', display: 'flex' }}>
                  <SecondaryTextTypography variant="body2" mb={0.5}>
                    See additional details for:
                  </SecondaryTextTypography>
                  <Text
                    variant="body2"
                    fontWeight="bold"
                    color="primary"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {footerText}
                  </Text>
                </Box>
                <OpenInNew
                  color="primary"
                  fontSize="small"
                  sx={{ position: 'absolute', right: 0, top: 0 }}
                />
              </DetailsBox>
            </AdditionalDetailsBox>
          )}

          {isExpandable && (
            <ExpandButton
              onClick={handleExpandToggle}
              mt={3}
              sx={{ cursor: 'pointer' }}
            >
              <Text
                variant="body2"
                fontWeight="bold"
                color="primary"
                lineHeight={1}
              >
                Close Details
              </Text>
              <KeyboardArrowUp
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </ExpandButton>
          )}
        </>
      ) : null}
    </CardContainer>
  );
};
